/**
 * CanvasList — the Workspace "Canvas" tab: a grid of shared whiteboards.
 * Click a card to open the full-screen editor; "New canvas" creates a blank
 * project and jumps straight into it. Rendered inside WorkspaceLayout.
 *
 * Organisation: boards can carry a folder + comma-separated tags and be
 * archived. The left rail filters by folder; search matches name + tags.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../../utils/toast';
import { Plus, PenTool, Trash2, Clock, User, Folder, FolderOpen, Archive, MoreVertical, Tag, ChevronRight, Check, Pencil, Share2, Users, Eye } from 'lucide-react';
import { T, PrimaryButton, SearchInput, ConfirmDialog } from '../../../components/em/ui';
import ShareDialog from '../../../components/em/ShareDialog';
import { wsBase } from '../wsApi';
import { fmtDate } from '../wsConstants';
import canvasApi from './canvasApi';
import askPrompt from '../../../components/em/askPrompt';

const parseTags = (s) => String(s || '').split(',').map((t) => t.trim()).filter(Boolean);

// Boards are private unless shared. The server tags each board with the
// caller's `access`: 'owner' / 'admin' can share, organise (folder, tags,
// archive) and delete; 'edit' / 'view' boards were shared with you (or reached
// through view_all_canvas) and live under "Shared with me".
const canManageBoard = (c) => !c.access || c.access === 'owner' || c.access === 'admin';

export default function CanvasList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [folder, setFolder] = useState('__all');      // '__all' | '__archived' | folderName
  const [menuId, setMenuId] = useState(null);
  const [menuView, setMenuView] = useState('root');   // 'root' | 'folders'
  const [folderMenu, setFolderMenu] = useState(null); // folder name whose menu is open
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [folderBusy, setFolderBusy] = useState(false);
  const [sharing, setSharing] = useState(null); // board whose Share dialog is open

  const closeMenu = useCallback(() => { setMenuId(null); setMenuView('root'); }, []);
  const closeFolderMenu = useCallback(() => setFolderMenu(null), []);

  const load = useCallback(() => {
    setLoading(true);
    canvasApi.list()
      .then(setItems)
      .catch(() => toast.error('Could not load canvases'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (menuId == null) return undefined;
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [menuId, closeMenu]);
  useEffect(() => {
    if (folderMenu == null) return undefined;
    document.addEventListener('click', closeFolderMenu);
    return () => document.removeEventListener('click', closeFolderMenu);
  }, [folderMenu, closeFolderMenu]);

  const create = async () => {
    setCreating(true);
    try {
      const rec = await canvasApi.create({ name: 'Untitled canvas' });
      navigate(`${wsBase()}/canvas/${rec.id}`);
    } catch {
      toast.error('Could not create canvas');
      setCreating(false);
    }
  };

  const [confirming, setConfirming] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const del = async () => {
    if (!confirming) return;
    setDeleting(true);
    try {
      await canvasApi.remove(confirming.id);
      setItems((p) => p.filter((x) => x.id !== confirming.id));
      toast.success('Canvas deleted');
      setConfirming(null);
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // Optimistic patch + persist for organise actions.
  const patch = async (id, body, label) => {
    setItems((p) => p.map((x) => (x.id === id ? { ...x, ...body } : x)));
    try { await canvasApi.update(id, body); if (label) toast.success(label); }
    catch { toast.error('Could not save'); load(); }
  };

  // Move: pick one of the folders that already exist (or clear the board's).
  const chooseFolder = (e, c, name) => {
    e.stopPropagation(); closeMenu();
    if ((c.folder || '') === name) return;   // already there — nothing to save
    patch(c.id, { folder: name }, name ? `Moved to "${name}"` : 'Removed from folder');
  };

  // Create: a folder is just a name carried by a board, so making one and
  // moving this board into it is the same action.
  const createFolder = async (e, c) => {
    e.stopPropagation(); closeMenu();
    const next = await askPrompt('New folder name:', '', {
      placeholder: 'e.g. Product',
      okLabel: 'Create folder',
    });
    if (next === null) return;
    const name = next.trim();
    if (!name) return;
    if (name === (c.folder || '')) return;
    patch(c.id, { folder: name }, `Moved to "${name}"`);
  };

  const editTags = async (e, c) => {
    e.stopPropagation(); closeMenu();
    const next = await askPrompt('Tags (comma-separated):', c.tags || '');
    if (next === null) return;
    patch(c.id, { tags: next }, 'Tags updated');
  };
  const toggleArchive = (e, c) => {
    e.stopPropagation(); closeMenu();
    patch(c.id, { archived: c.archived ? 0 : 1 }, c.archived ? 'Unarchived' : 'Archived');
  };

  /* ── Folder rename / delete ────────────────────────────────────────────
     A folder is not a record — it is the `folder` string on each board. So
     renaming one rewrites that string on every board carrying it, and deleting
     one clears the string. Neither touches a board itself. */
  const boardsInFolder = useCallback(
    (name) => items.filter((c) => c.folder === name),
    [items],
  );

  const applyToFolder = async (name, nextFolder, label) => {
    const boards = boardsInFolder(name);
    if (!boards.length) return;
    setItems((prev) =>
      prev.map((c) => (c.folder === name ? { ...c, folder: nextFolder } : c)),
    );
    // Follow the folder if the rail was filtered to it, so the grid doesn't
    // silently empty out under a name that no longer exists.
    setFolder((f) => (f === name ? (nextFolder || '__all') : f));
    try {
      await Promise.all(boards.map((c) => canvasApi.update(c.id, { folder: nextFolder })));
      toast.success(label);
    } catch {
      toast.error('Could not save');
      load();
    }
  };

  const renameFolder = async (e, name) => {
    e.stopPropagation(); closeFolderMenu();
    const next = await askPrompt('Rename folder', name, {
      placeholder: 'Folder name',
      okLabel: 'Rename',
      required: true,
    });
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === name) return;
    const merging = folderNames.includes(trimmed);
    if (merging && !window.confirm(`"${trimmed}" already exists. Merge "${name}" into it?`)) return;
    applyToFolder(name, trimmed, merging ? `Merged into "${trimmed}"` : `Renamed to "${trimmed}"`);
  };

  const deleteFolder = (e, name) => {
    e.stopPropagation(); closeFolderMenu();
    setDeletingFolder({ name, count: boardsInFolder(name).length });
  };

  // Folders / archive organise the boards you own (or administer). Boards
  // shared with you carry the owner's labels, not yours, so they stay out of
  // the folder rail and show under "All boards" and "Shared with me".
  const managed = useMemo(() => items.filter(canManageBoard), [items]);
  const sharedWithMe = useMemo(() => items.filter((c) => !canManageBoard(c)), [items]);

  // Folder list (derived) with counts of non-archived boards.
  const folders = useMemo(() => {
    const m = new Map();
    managed.forEach((c) => { if (!c.archived && c.folder) m.set(c.folder, (m.get(c.folder) || 0) + 1); });
    return [...m.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
  }, [managed]);
  const archivedCount = useMemo(() => managed.filter((c) => c.archived).length, [managed]);

  // Every folder name in use, for the move picker. Unlike the rail above this
  // counts archived boards too — a folder that currently holds only archived
  // boards is still a real destination.
  const folderNames = useMemo(
    () => [...new Set(managed.map((c) => c.folder).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [managed],
  );

  const matchesSearch = (c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || parseTags(c.tags).some((t) => t.toLowerCase().includes(q));
  };
  const filtered = items.filter((c) => {
    if (!matchesSearch(c)) return false;
    if (!canManageBoard(c)) return folder === '__all' || folder === '__shared';
    if (folder === '__shared') return false;
    if (folder === '__archived') { if (!c.archived) return false; }
    else if (c.archived) return false;
    if (folder !== '__all' && folder !== '__archived' && c.folder !== folder) return false;
    return true;
  });

  const openShare = (e, c) => { e.stopPropagation(); closeMenu(); setSharing(c); };

  // `manage` names a real folder and swaps the count for a ⋯ menu. The built-in
  // rows (All boards, Archived) pass none — they aren't folders, so there is
  // nothing there to rename or delete.
  const FolderBtn = ({ active, onClick, icon: Icon, children, count, manage }) => (
    <div
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = T.bgMuted; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      style={{ position: 'relative', display: 'flex', alignItems: 'center', borderRadius: 9,
        background: active ? T.orange50 : 'transparent', transition: 'background 120ms' }}
    >
      <button onClick={onClick}
        style={{ display: 'flex', alignItems: 'center', gap: 9, flex: 1, minWidth: 0, textAlign: 'left',
          padding: '8px 11px', borderRadius: 9, border: 0, cursor: 'pointer', background: 'transparent',
          color: active ? T.orange : T.fg2, font: `${active ? 600 : 500} 13px ${T.font}` }}>
        <Icon size={15} style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
      </button>

      {manage ? (
        <>
          {/* Sits in a bordered chip: three pale dots alone at the edge of the
              rail read as decoration, not a control. */}
          <button
            aria-label={`Manage folder ${manage}`}
            title="Rename or delete"
            onClick={(e) => { e.stopPropagation(); setFolderMenu(folderMenu === manage ? null : manage); }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.fg4; }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26,
              // padding/margin/line-height are reset explicitly: the global
              // `button` rule in index.css forces `padding: 0 16px`, which on a
              // 26px-wide button collapses the content box to nothing and
              // squeezes the icon out of view entirely.
              padding: 0, margin: '0 6px 0 0', lineHeight: 1,
              flexShrink: 0, borderRadius: 7, cursor: 'pointer',
              // Always drawn, never hover-only: the control has to be findable
              // without knowing it is there.
              border: `1px solid ${folderMenu === manage ? T.orange : T.border}`,
              background: 'var(--em-bg-card)',
              color: folderMenu === manage ? T.orange : T.fg4 }}
          >
            <MoreVertical size={16} strokeWidth={2.5} />
          </button>
          {folderMenu === manage && (
            <div onClick={(e) => e.stopPropagation()}
              style={{ position: 'absolute', right: 4, top: '100%', zIndex: 30, minWidth: 150,
                background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 10,
                boxShadow: '0 12px 32px -8px rgba(10,10,10,0.22)', padding: 5 }}>
              <MenuItem icon={Pencil} onClick={(e) => renameFolder(e, manage)}>Rename…</MenuItem>
              <MenuItem icon={Trash2} danger onClick={(e) => deleteFolder(e, manage)}>Delete folder</MenuItem>
            </div>
          )}
        </>
      ) : (
        count != null && (
          <span style={{ font: `600 11px ${T.font}`, color: T.fg7, paddingRight: 11 }}>{count}</span>
        )
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      {/* Folder rail */}
      <aside style={{ width: 190, flexShrink: 0, position: 'sticky', top: 16 }}>
        <div style={{ font: `700 11px ${T.font}`, color: T.fg7, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '4px 0 8px 11px' }}>Folders</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FolderBtn active={folder === '__all'} onClick={() => setFolder('__all')} icon={FolderOpen}
            count={managed.filter((c) => !c.archived).length + sharedWithMe.length}>All boards</FolderBtn>
          {sharedWithMe.length > 0 && (
            <FolderBtn active={folder === '__shared'} onClick={() => setFolder('__shared')} icon={Users}
              count={sharedWithMe.length}>Shared with me</FolderBtn>
          )}
          {folders.map((f) => (
            <FolderBtn key={f.name} active={folder === f.name} onClick={() => setFolder(f.name)} icon={Folder} manage={f.name}>{f.name}</FolderBtn>
          ))}
          {archivedCount > 0 && (
            <FolderBtn active={folder === '__archived'} onClick={() => setFolder('__archived')} icon={Archive} count={archivedCount}>Archived</FolderBtn>
          )}
        </div>
      </aside>

      {/* Main */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or #tag…" style={{ minWidth: 220 }} />
          <div style={{ flex: 1 }} />
          <PrimaryButton icon={Plus} onClick={create} disabled={creating}>{creating ? 'Creating…' : 'New canvas'}</PrimaryButton>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: T.fg7 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: T.fg7 }}>
            <PenTool size={40} color={T.fg7} style={{ opacity: 0.5 }} />
            <div style={{ font: `600 15px ${T.font}`, color: T.fg1, marginTop: 12 }}>
              {search || folder !== '__all' ? 'No canvases match this view' : 'No canvases yet'}
            </div>
            <div style={{ font: `400 13px ${T.font}`, marginTop: 4 }}>
              Create a whiteboard to sketch ideas, diagrams and flows with your team.
            </div>
            {!search && folder === '__all' && <div style={{ marginTop: 16, display: 'inline-flex' }}><PrimaryButton icon={Plus} onClick={create}>New canvas</PrimaryButton></div>}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {filtered.map((c) => {
              const tags = parseTags(c.tags);
              return (
                <div key={c.id}
                  onClick={() => navigate(`${wsBase()}/canvas/${c.id}`)}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 24px -10px rgba(10,10,10,0.18)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = T.shadowSm; e.currentTarget.style.transform = 'none'; }}
                  // While its menu is open the card is lifted above the other
                  // cards AND above the fixed bottom dock (z-index 60 in
                  // WorkspaceLayout), which otherwise paints over the menu.
                  style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: T.shadowSm, cursor: 'pointer', transition: 'box-shadow 150ms, transform 150ms', opacity: c.archived ? 0.7 : 1, position: 'relative', zIndex: menuId === c.id ? 80 : undefined }}>
                  {/* Thumbnail — rounds its own top corners so the card itself
                      doesn't need overflow:hidden (which would clip the organise menu below). */}
                  <div style={{ height: 150, borderRadius: '13px 13px 0 0', background: 'repeating-conic-gradient(#eef1f5 0% 25%, #f7f9fb 0% 50%) 50% / 18px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {c.thumbnail
                      ? <img src={c.thumbnail} alt={c.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      : <PenTool size={30} color={T.fg7} style={{ opacity: 0.4 }} />}
                  </div>
                  {/* Meta */}
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0, font: `700 14px ${T.font}`, color: T.fg1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name || 'Untitled canvas'}</div>
                      {/* Actions — share / organise / delete are the owner's (or
                          an Admin's); a board shared with you has none. */}
                      {canManageBoard(c) && (
                      <div style={{ position: 'relative' }}>
                        <button onClick={(e) => { e.stopPropagation(); setMenuView('root'); setMenuId(menuId === c.id ? null : c.id); }} title="Actions"
                          style={{ background: 'none', border: 0, cursor: 'pointer', color: T.fg7, display: 'flex', padding: 2 }}><MoreVertical size={15} /></button>
                        {menuId === c.id && (
                          <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20, minWidth: 190, maxWidth: 260, background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 12px 32px -8px rgba(10,10,10,0.22)', padding: 5 }}>
                            <MenuItem icon={Share2} onClick={(e) => openShare(e, c)}
                              trailing={Number(c.share_count) > 0 ? <span style={{ font: `600 11px ${T.font}`, color: T.fg7 }}>{c.share_count}</span> : null}>
                              Share…
                            </MenuItem>
                            <div style={{ height: 1, background: T.divider, margin: '5px 2px' }} />
                            <MenuItem icon={Folder} active={menuView === 'folders'}
                              onClick={(e) => { e.stopPropagation(); setMenuView(menuView === 'folders' ? 'root' : 'folders'); }}
                              trailing={<ChevronRight size={13} color={T.fg7} />}>
                              Move to folder
                            </MenuItem>
                            <MenuItem icon={Tag} onClick={(e) => editTags(e, c)}>Edit tags…</MenuItem>
                            <MenuItem icon={Archive} onClick={(e) => toggleArchive(e, c)}>{c.archived ? 'Unarchive' : 'Archive'}</MenuItem>
                            <div style={{ height: 1, background: T.divider, margin: '5px 2px' }} />
                            <MenuItem icon={Trash2} danger onClick={(e) => { e.stopPropagation(); setConfirming(c); }}>Delete</MenuItem>

                            {/* Folder flyout — opens beside the menu, so the board's
                                other actions stay on screen while picking. */}
                            {menuView === 'folders' && (
                              <FolderFlyout
                                folders={folderNames}
                                current={c.folder || ''}
                                onPick={(e, name) => chooseFolder(e, c, name)}
                                onCreate={(e) => createFolder(e, c)}
                              />
                            )}
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                    {/* Sharing state: my board → who it's shared with; someone
                        else's → my access to it. */}
                    {canManageBoard(c) ? (
                      Number(c.share_count) > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, font: `600 10.5px ${T.font}`, color: '#008236', background: '#dcfce7', borderRadius: 6, padding: '2px 7px' }}>
                            <Users size={10} /> Shared with {c.share_count}
                          </span>
                        </div>
                      )
                    ) : (
                      <div style={{ marginTop: 8 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, font: `600 10.5px ${T.font}`, color: c.access === 'edit' ? T.orange : T.fg5, background: c.access === 'edit' ? T.orange50 : T.bgMuted, borderRadius: 6, padding: '2px 7px' }}>
                          {c.access === 'edit' ? <><Pencil size={10} /> Can edit</> : <><Eye size={10} /> Read only</>}
                        </span>
                      </div>
                    )}
                    {canManageBoard(c) && (c.folder || tags.length > 0) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        {c.folder && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, font: `600 10.5px ${T.font}`, color: T.fg5, background: T.bgMuted, borderRadius: 6, padding: '2px 7px' }}><Folder size={10} /> {c.folder}</span>}
                        {tags.slice(0, 3).map((t) => (
                          <span key={t} style={{ font: `600 10.5px ${T.font}`, color: T.orange, background: T.orange50, borderRadius: 6, padding: '2px 7px' }}>#{t}</span>
                        ))}
                        {tags.length > 3 && <span style={{ font: `600 10.5px ${T.font}`, color: T.fg7 }}>+{tags.length - 3}</span>}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 9, font: `500 11px ${T.font}`, color: T.fg7 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                        <User size={12} /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.created_by_name || 'Unknown'}</span>
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 'auto', flexShrink: 0 }}>
                        <Clock size={12} /> {fmtDate(c.updated_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {sharing && (
        <ShareDialog
          title="Share canvas"
          subtitle={sharing.name || 'Untitled canvas'}
          api={{
            listColleagues: () => canvasApi.listColleagues(),
            getShares: () => canvasApi.getShares(sharing.id),
            share: (userIds) => canvasApi.share(sharing.id, userIds),
            setPermission: (userId, permission) => canvasApi.setSharePermission(sharing.id, userId, permission),
            unshare: (userId) => canvasApi.unshare(sharing.id, userId),
          }}
          onClose={() => setSharing(null)}
          onCountChange={(n) => setItems((p) => p.map((x) => (x.id === sharing.id ? { ...x, share_count: n } : x)))}
        />
      )}

      <ConfirmDialog
        open={!!confirming}
        title="Delete canvas?"
        message={`"${confirming?.name || 'This canvas'}" and its history will be removed. This cannot be undone.`}
        confirmLabel="Delete canvas"
        danger
        busy={deleting}
        onConfirm={del}
        onCancel={() => !deleting && setConfirming(null)}
      />

      {/* Deleting a folder removes the label, never the boards — say so, since
          "Delete folder" next to a list of boards reads like it takes them. */}
      <ConfirmDialog
        open={!!deletingFolder}
        title={`Delete folder "${deletingFolder?.name || ''}"?`}
        message={
          deletingFolder?.count
            ? `The ${deletingFolder.count} board${deletingFolder.count === 1 ? '' : 's'} in it will be kept and moved out of the folder.`
            : 'The folder is empty and will simply disappear.'
        }
        confirmLabel="Delete folder"
        danger
        busy={folderBusy}
        onConfirm={async () => {
          if (!deletingFolder) return;
          setFolderBusy(true);
          await applyToFolder(deletingFolder.name, '', `Folder "${deletingFolder.name}" deleted`);
          setFolderBusy(false);
          setDeletingFolder(null);
        }}
        onCancel={() => !folderBusy && setDeletingFolder(null)}
      />
    </div>
  );
}

function MenuItem({ icon: Icon, children, onClick, danger, accent, active, trailing, title }) {
  const color = danger ? T.danger : accent ? T.orange : T.fg2;
  const rest = active ? T.bgMuted : 'transparent';
  return (
    <button onClick={onClick} title={title}
      style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 7,
        border: 0, cursor: 'pointer', background: rest, color, font: `${accent ? 600 : 500} 12.5px ${T.font}` }}
      onMouseEnter={(e) => { e.currentTarget.style.background = T.bgMuted; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = rest; }}>
      <Icon size={14} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
      {trailing && <span style={{ display: 'flex', flexShrink: 0 }}>{trailing}</span>}
    </button>
  );
}

/**
 * Side panel listing the folders that already exist. Sits to the right of the
 * board menu, flipping to the left edge when the card is too close to the
 * viewport's right side for it to fit.
 */
function FolderFlyout({ folders, current, onPick, onCreate }) {
  const ref = useRef(null);
  // `flip` swaps it to the left of the menu; `lift` pulls it up so the last
  // row clears the bottom of the window.
  const [{ flip, lift }, setPlace] = useState({ flip: false, lift: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Measured in its default position; the effect does not depend on the
    // values it sets, so this settles in one pass.
    const r = el.getBoundingClientRect();
    const MARGIN = 12;
    setPlace({
      flip: r.right > window.innerWidth - MARGIN,
      // Never lift so far that the top goes off screen.
      lift: Math.max(0, Math.min(r.bottom - (window.innerHeight - MARGIN), r.top - MARGIN)),
    });
  }, []);

  return (
    <div ref={ref} onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', top: -5 - lift, zIndex: 21,
        ...(flip ? { right: '100%', marginRight: 6 } : { left: '100%', marginLeft: 6 }),
        minWidth: 180, maxWidth: 240,
        background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 10,
        boxShadow: '0 12px 32px -8px rgba(10,10,10,0.22)', padding: 5,
      }}>
      <div style={{ padding: '7px 10px 5px', font: `700 10.5px ${T.font}`, color: T.fg7, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Move to
      </div>

      {folders.length === 0 ? (
        <div style={{ padding: '4px 10px 8px', font: `400 12px ${T.font}`, color: T.fg7 }}>No folders yet.</div>
      ) : (
        <div style={{ maxHeight: 'min(210px, 40vh)', overflowY: 'auto' }}>
          {folders.map((name) => {
            const isCurrent = current === name;
            return (
              // Clicking the folder it is already in takes the board back out,
              // which is the only way to unfile it from this panel.
              <MenuItem key={name} icon={Folder} active={isCurrent}
                title={isCurrent ? 'Remove from this folder' : `Move to ${name}`}
                onClick={(e) => onPick(e, isCurrent ? '' : name)}
                trailing={isCurrent ? <Check size={14} color={T.orange} /> : null}>
                {name}
              </MenuItem>
            );
          })}
        </div>
      )}

      <div style={{ height: 1, background: T.divider, margin: '5px 2px' }} />
      <MenuItem icon={Plus} accent onClick={onCreate}>New folder…</MenuItem>
    </div>
  );
}
